import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from "@repo/components/src/ui/form-control";
import { Pressable } from "@repo/components/src/ui/pressable";
import { AlertCircleIcon } from "lucide-react-native";
import { ComponentProps } from "react";
import { Controller, FieldValues } from "react-hook-form";
import { Badge, BadgeText } from "../../ui/badge";
import { HStack } from "../../ui/hstack";
import { focusScrollRef } from "../focusScrollRef";
import { FormInputSelectProps } from "../types";

const defaultSelectedProps: ComponentProps<typeof Badge> = {
  variant: "outline",
  action: "success",
};
const defaultNonSelectedProps: ComponentProps<typeof Badge> = {
  variant: "outline",
  action: "muted",
};

type FormInputBadgeProps<TFieldValues extends FieldValues> =
  FormInputSelectProps<TFieldValues, string | number> & {
    selectedProps?: ComponentProps<typeof Badge>;
    nonSelectedProps?: ComponentProps<typeof Badge>;
    space?: ComponentProps<typeof HStack>["space"];
  };

export default function FormInputBadge<TFieldValues extends FieldValues>(
  props: FormInputBadgeProps<TFieldValues>,
) {
  const {
    control,
    name,
    label,
    required,
    disabled,
    className,
    size,
    options,
    labelSize,
    space = "sm",
    selectedProps = defaultSelectedProps,
    nonSelectedProps = defaultNonSelectedProps,
  } = props;
  return (
    <Controller
      name={name}
      disabled={disabled}
      control={control}
      render={({
        field: { onChange, value, ref, disabled },
        fieldState: { error },
        formState: { isSubmitting },
      }) => (
        <FormControl
          isInvalid={!!error}
          isRequired={required}
          isDisabled={disabled || isSubmitting}
          className={className}
          size={size}
        >
          {!!label && (
            <FormControlLabel>
              <FormControlLabelText size={labelSize || size}>
                {label}
              </FormControlLabelText>
            </FormControlLabel>
          )}
          <HStack space={space} ref={focusScrollRef(ref)}>
            {options.map((option) => {
              const isSelected = value === option.value;
              const extraProps = isSelected ? selectedProps : nonSelectedProps;
              return (
                <Pressable
                  key={option.value}
                  disabled={disabled || isSubmitting}
                  onPress={() => onChange(option.value)}
                >
                  {() => (
                    <Badge
                      {...extraProps}
                      className={`${extraProps.className ?? ""}`}
                    >
                      <BadgeText>{option.label}</BadgeText>
                    </Badge>
                  )}
                </Pressable>
              );
            })}
          </HStack>
          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} className="mr-1" />
            <FormControlErrorText>{error?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
